import { useState } from 'react'
import { Plus, Scale } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { Select } from '@/components/ui/select'
import {
  useAppraisalCycles, useCalibrationMutations, useCalibrationSessions, useCycleAppraisals,
} from '../api'

/**
 * Calibration sessions.
 *
 * The screen shows before and after side by side, because the disagreement is the content of
 * the meeting: a manager rated somebody one thing, a room concluded another, and the reason
 * is the only part anybody can learn from afterwards.
 */
export function CalibrationPage() {
  useBreadcrumb()
  const sessions = useCalibrationSessions()
  const cycles = useAppraisalCycles()
  const { open, adjust, complete } = useCalibrationMutations()

  const [sessionDraft, setSessionDraft] = useState<{ cycleId: string; name: string } | null>(null)
  const [adjustDraft, setAdjustDraft] = useState<{
    sessionId: string; cycleId: string; appraisalId: string; actingEmployeeId: string
    scoreAfter: string; rationale: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const appraisalsForAdjust = useCycleAppraisals(adjustDraft?.cycleId ?? null)

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Calibration"
        subtitle="Moderating ratings across a cycle, and what each meeting changed."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setSessionDraft({ cycleId: cycles.data?.[0]?.id ?? '', name: '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New session
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Scale className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Nobody may moderate a review they wrote — checking one manager's standard against the
        room's is what calibration is for. The manager's original figure is kept beside the
        adjustment rather than replaced, so a rating that moved reads as a rating that moved.
      </p>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {sessions.isPending && <Skeleton className="h-32" />}
      {sessions.isError && (
        <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
      )}
      {sessions.isSuccess && sessions.data.length === 0 && (
        <Card>
          <EmptyState
            title="No calibration session"
            description="A session takes one cycle's appraisals and moderates them as a group."
          />
        </Card>
      )}

      {sessions.isSuccess && sessions.data.map((session) => (
        <Card key={session.id} className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold">{session.name}</h2>
              <p className="text-xs text-slate-600">{session.cycleName}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge
                status={session.status}
                tone={session.status === 'COMPLETED' ? 'success' : 'info'}
              />
              {session.status === 'IN_PROGRESS' && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setError(null)
                      setAdjustDraft({
                        sessionId: session.id, cycleId: session.cycleId, appraisalId: '',
                        actingEmployeeId: '', scoreAfter: '', rationale: '',
                      })
                    }}
                  >
                    Adjust a rating
                  </Button>
                  <Button
                    type="button"
                    onClick={() => act(
                      () => complete.mutateAsync({ id: session.id }),
                      'That session could not be completed')}
                  >
                    Complete
                  </Button>
                </>
              )}
            </div>
          </div>

          {session.entries.length === 0 ? (
            <p className="mt-3 text-xs text-slate-600">Nothing has been adjusted yet.</p>
          ) : (
            <div className="mt-3 overflow-x-auto overscroll-x-contain">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="p-2 text-start font-medium">Person</th>
                    <th className="p-2 text-end font-medium">Manager rated</th>
                    <th className="p-2 text-end font-medium">Meeting concluded</th>
                    <th className="p-2 text-start font-medium">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {session.entries.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-100 align-top">
                      <td className="p-2">{entry.employeeName ?? '—'}</td>
                      <td className="p-2 text-end">{entry.scoreBefore ?? '—'}</td>
                      <td className="p-2 text-end font-medium">
                        {entry.scoreAfter ?? '—'}
                        {!entry.changed && (
                          <span className="block text-xs font-normal text-slate-600">
                            unchanged
                          </span>
                        )}
                      </td>
                      <td className="p-2 max-w-md text-xs text-slate-600">
                        {entry.rationale ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}

      <FormDialog
        open={sessionDraft != null}
        onOpenChange={(open) => { if (!open) setSessionDraft(null) }}
        title="New calibration session"
        submitLabel="Open session"
        loading={open.isPending}
        error={error}
        onSubmit={() => {
          if (!sessionDraft) return
          act(async () => {
            await open.mutateAsync({ cycleId: sessionDraft.cycleId, name: sessionDraft.name })
            setSessionDraft(null)
          }, 'That session could not be opened')
        }}
      >
        {sessionDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="session-cycle">Cycle</Label>
              <Select
                id="session-cycle"
                className="w-full"
                value={sessionDraft.cycleId}
                onChange={(event) =>
                  setSessionDraft({ ...sessionDraft, cycleId: event.target.value })}
              >
                <option value="">Choose a cycle…</option>
                {cycles.data?.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="session-name">Name</Label>
              <Input
                id="session-name" value={sessionDraft.name}
                onChange={(event) =>
                  setSessionDraft({ ...sessionDraft, name: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={adjustDraft != null}
        onOpenChange={(open) => { if (!open) setAdjustDraft(null) }}
        size="lg"
        title="Adjust a rating"
        description="The original is kept. Say why: the person whose rating changed is entitled to the reason."
        submitLabel="Record the adjustment"
        loading={adjust.isPending}
        error={error}
        onSubmit={() => {
          if (!adjustDraft) return
          act(async () => {
            await adjust.mutateAsync({
              id: adjustDraft.sessionId,
              body: {
                appraisalId: adjustDraft.appraisalId,
                actingEmployeeId: adjustDraft.actingEmployeeId || null,
                scoreAfter: adjustDraft.scoreAfter ? Number(adjustDraft.scoreAfter) : null,
                rationale: adjustDraft.rationale || null,
              },
            })
            setAdjustDraft(null)
          }, 'That adjustment could not be recorded')
        }}
      >
        {adjustDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="adjust-appraisal">Appraisal</Label>
              <Select
                id="adjust-appraisal"
                className="w-full"
                value={adjustDraft.appraisalId}
                onChange={(event) =>
                  setAdjustDraft({ ...adjustDraft, appraisalId: event.target.value })}
              >
                <option value="">Choose an appraisal…</option>
                {appraisalsForAdjust.data?.map((appraisal) => (
                  <option key={appraisal.id} value={appraisal.id}>
                    {appraisal.employeeName} — {appraisal.finalScore ?? 'not scored'}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="adjust-actor">Who is moderating</Label>
              <EmployeePicker
                  id="adjust-actor"
                  value={adjustDraft.actingEmployeeId}
                  onChange={(employeeId) => setAdjustDraft({ ...adjustDraft, actingEmployeeId: employeeId ?? '' })}
                />
              <p className="mt-1 text-xs text-slate-600">
                Anybody who wrote a review on this appraisal is refused.
              </p>
            </div>
            <div>
              <Label htmlFor="adjust-score">Concluded score</Label>
              <Input
                id="adjust-score" type="number" value={adjustDraft.scoreAfter}
                onChange={(event) =>
                  setAdjustDraft({ ...adjustDraft, scoreAfter: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="adjust-rationale">Why it moved</Label>
              <Input
                id="adjust-rationale" value={adjustDraft.rationale}
                onChange={(event) =>
                  setAdjustDraft({ ...adjustDraft, rationale: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
